import type { InternalAxiosRequestConfig, AxiosError } from 'axios';
import type { AxiosInstance } from 'axios';
import {
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  AUTH_USER_KEY,
  AUTH_LOGGED_IN_KEY,
} from './config';
import { getApiErrorHandlers } from './errorHandlers';
import { authService } from '../services/AuthService';
import { notifyBannedFromApi } from './bannedSync';
import { attachFriendlyApiErrorMessage } from '../../utils/apiErrorMessages';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;

const SESSION_ENDED_MESSAGE =
  'Oturumunuz sonlanmıştır, lütfen tekrar giriş yapın.';

function getStoredToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function clearAuthStorage(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_LOGGED_IN_KEY);
}

function setTokens(token: string, refreshToken?: string | null): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  if (refreshToken != null) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

/**
 * Request interceptor: inject auth token when present.
 */
export function authInterceptor(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

/** Result: success, or failure with whether we attempted refresh (for message). */
type RefreshResult = { success: true } | { success: false; attemptedRefresh: boolean };

/** In-flight refresh promise so concurrent 401s trigger a single refresh. */
let refreshPromise: Promise<RefreshResult> | null = null;

function attemptRefresh(axiosInstance: AxiosInstance): Promise<RefreshResult> {
  if (refreshPromise !== null) {
    return refreshPromise;
  }
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return Promise.resolve({ success: false, attemptedRefresh: false });
  }
  const promise = authService
    .refreshToken({ refreshToken })
    .then((result): RefreshResult => {
      refreshPromise = null;
      const newAuthToken = result.data?.authToken ?? result.data?.token;
      if (result.isSuccess && newAuthToken) {
        setTokens(newAuthToken, result.data?.refreshToken ?? undefined);
        console.log('Token is refreshed.');
        return { success: true };
      }
      console.log('Token refresh is attempted but failed.');
      return { success: false, attemptedRefresh: true };
    })
    .catch((): RefreshResult => {
      refreshPromise = null;
      console.log('Token refresh is attempted but failed.');
      return { success: false, attemptedRefresh: true };
    });
  refreshPromise = promise;
  return promise;
}

function isRefreshRequest(config: InternalAxiosRequestConfig | undefined): boolean {
  const url = config?.url ?? '';
  return typeof url === 'string' && url.includes('refresh-token');
}

/**
 * Response error interceptor: 401 → try refresh → retry or logout; 403/404 → callbacks.
 * No UI here – invokes registered callbacks.
 */
function createErrorInterceptor(axiosInstance: AxiosInstance) {
  return function errorInterceptor(error: AxiosError): Promise<never> {
    attachFriendlyApiErrorMessage(error);
    const status = error.response?.status;
    const handlers = getApiErrorHandlers();

    if (status === HTTP_UNAUTHORIZED) {
      if (isRefreshRequest(error.config)) {
        clearAuthStorage();
        handlers.onUnauthorized?.(SESSION_ENDED_MESSAGE);
        return Promise.reject(error);
      }
      return attemptRefresh(axiosInstance)
        .then((result) => {
          if (result.success && error.config) {
            const newToken = getStoredToken();
            if (newToken) {
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return axiosInstance.request(error.config);
            }
          }
          if (!result.success) {
            clearAuthStorage();
            const message = result.attemptedRefresh ? SESSION_ENDED_MESSAGE : undefined;
            handlers.onUnauthorized?.(message);
          }
          return Promise.reject(error);
        })
        .catch(() => {
          clearAuthStorage();
          handlers.onUnauthorized?.(SESSION_ENDED_MESSAGE);
          return Promise.reject(error);
        }) as Promise<never>;
    }

    if (status === HTTP_FORBIDDEN) {
      const data = error.response?.data as { code?: string } | undefined;
      if (data?.code === 'ACCOUNT_BANNED') {
        notifyBannedFromApi();
      }
      handlers.onForbidden?.();
    } else if (status === HTTP_NOT_FOUND) {
      handlers.onNotFound?.();
    }

    return Promise.reject(error);
  };
}

export { createErrorInterceptor };
